import { IMAGE_HEADER_LIMIT, parseImageHeader, UnknownDimensionsError, type Dimensions, type PageSource } from '@tanko/shared'
import { imageFileNames, imageMediaType } from '../lib/fs-access'

type DimensionLookup = (page: number) => Promise<Dimensions | null | undefined>

export class FolderSource implements PageSource {
  readonly pageCount: number
  private directory: FileSystemDirectoryHandle | null
  private names: string[]
  private lookup?: DimensionLookup

  private constructor(directory: FileSystemDirectoryHandle, names: string[], lookup?: DimensionLookup) {
    this.directory = directory
    this.names = names
    this.pageCount = names.length
    this.lookup = lookup
  }

  static async create(directory: FileSystemDirectoryHandle, lookup?: DimensionLookup): Promise<FolderSource> {
    return new FolderSource(directory, await imageFileNames(directory), lookup)
  }

  private validate(i: number): void {
    if (!this.directory) throw new Error('FolderSource 已釋放（disposed）')
    if (!Number.isInteger(i) || i < 0 || i >= this.pageCount) throw new RangeError(`頁碼超出範圍：${i}`)
  }

  async getPage(i: number): Promise<Blob> {
    this.validate(i)
    const directory = this.directory!
    const name = this.names[i]
    try {
      const handle = await directory.getFileHandle(name)
      const file = await handle.getFile()
      this.validate(i)
      return file.type ? file : file.slice(0, file.size, imageMediaType(name))
    } catch (cause) {
      this.validate(i)
      const error = new Error(`無法讀取第 ${i + 1} 頁（${name}）：檔案可能已遺失或無權限。`, { cause })
      error.name = 'PageReadError'
      throw error
    }
  }

  async getDimensions(i: number): Promise<Dimensions> {
    this.validate(i)
    const cached = await this.lookup?.(i)
    this.validate(i)
    if (cached === null) throw new UnknownDimensionsError(i)
    if (cached) return cached
    const file = await this.getPage(i)
    const result = parseImageHeader(await file.slice(0, IMAGE_HEADER_LIMIT).arrayBuffer())
    this.validate(i)
    if (!result) throw new UnknownDimensionsError(i)
    return result
  }

  async dispose(): Promise<void> {
    this.directory = null
    this.names = []
    this.lookup = undefined
  }
}
